import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Input } from '../components/ui/Input.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TourForm {
  name: string;
  description: string;
  passEnabled: boolean;
  imageUrl: string;
}

const EMPTY_FORM: TourForm = {
  name: '',
  description: '',
  passEnabled: false,
  imageUrl: '',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TourFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [form, setForm] = useState<TourForm>(EMPTY_FORM);

  const { data: existing } = useQuery({
    queryKey: ['tour-form', id],
    queryFn: () =>
      api.get(`/api/v1/events/tours/${id}`).then((r) => r.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing) {
      setForm({
        name: existing.name ?? '',
        description: existing.description ?? '',
        passEnabled: existing.passEnabled ?? false,
        imageUrl: existing.imageUrl ?? '',
      });
    }
  }, [existing]);

  const saveMutation = useMutation({
    mutationFn: (body: object) =>
      isEdit
        ? api.put(`/api/v1/events/tours/${id}`, body).then((r) => r.data)
        : api.post('/api/v1/events/tours', body).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['tours'] });
      toast.success(isEdit ? 'Tour aggiornato' : 'Tour creato');
      navigate(`/events/tours/${isEdit ? id : data.id}`);
    },
    onError: (err) => toast.error(extractError(err, 'Errore durante il salvataggio')),
  });

  function handleSubmit() {
    if (!form.name) return;
    saveMutation.mutate({
      name: form.name,
      description: form.description || undefined,
      passEnabled: form.passEnabled,
      imageUrl: form.imageUrl || undefined,
    });
  }

  function set<K extends keyof TourForm>(key: K, value: TourForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  return (
    <div>
      <PageHeader title={isEdit ? 'Modifica tour' : 'Nuovo tour'} />

      <div className="max-w-xl bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
        <Input
          label="Nome *"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="Nome del tour"
        />

        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1">Descrizione</label>
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={3}
            placeholder="Descrizione opzionale"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-300 resize-none"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            id="passEnabled"
            type="checkbox"
            checked={form.passEnabled}
            onChange={(e) => set('passEnabled', e.target.checked)}
            className="w-4 h-4 accent-zinc-800"
          />
          <label htmlFor="passEnabled" className="text-sm text-zinc-700 cursor-pointer">
            Abilita pass tour (iscrizione al tour completo)
          </label>
        </div>

        <Input
          label="URL immagine"
          value={form.imageUrl}
          onChange={(e) => set('imageUrl', e.target.value)}
          placeholder="https://..."
        />

        {saveMutation.isError && (
          <p className="text-xs text-red-500">{extractError(saveMutation.error)}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            Annulla
          </Button>
          <Button
            loading={saveMutation.isPending}
            disabled={!form.name}
            onClick={handleSubmit}
          >
            {isEdit ? 'Salva modifiche' : 'Crea tour'}
          </Button>
        </div>
      </div>
    </div>
  );
}
